import React, { useMemo, useState, useEffect } from "react";
import { FaCogs } from "react-icons/fa";
import {
  EmptyState,
  ListView,
  ListToolbar,
  FilterTabs,
} from "../../shared";
import { Pagination } from "../../atoms";
import { useDebounce } from "../../../Hooks/useDebounce";
import { getPartMeta } from "../../../utils/partsMeta";

// Limit varsa 0-100 yüzde döndür; limit yoksa null (çubuk gösterilmez).
const pct = (value, limit) => {
  const lim = Number(limit) || 0;
  if (lim <= 0) return null;
  return Math.min(100, Math.round((Number(value || 0) / lim) * 100));
};

const barColor = (p) =>
  p > 80 ? "bg-red-400" : p > 50 ? "bg-amber-400" : "bg-emerald-400";

const fmtDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString("tr-TR");
  } catch (_) {
    return null;
  }
};

const num = (v) => Number(v || 0).toLocaleString("tr-TR");

const PAGE_SIZE = 25;

const STATUS_FILTERS = [
  { id: "all", label: "Tümü" },
  { id: "active", label: "Aktif" },
  { id: "fault", label: "Arızalı" },
];

/**
 * Kullanım hücresi — değer/limit + limit varsa ilerleme çubuğu.
 * Limit tanımlı değilse çubuk çizilmez; %0 çubuk "ölçülüyor" izlenimi verirdi.
 */
const UsageCell = ({ value, limit }) => {
  const p = pct(value, limit);
  const hasLimit = Number(limit) > 0;
  return (
    <div className="min-w-[120px]">
      <div className="text-sm font-semibold text-gray-800">
        {num(value)}
        {hasLimit && <span className="text-gray-400"> / {num(limit)}</span>}
      </div>
      {p !== null && (
        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${barColor(p)}`}
              style={{ width: `${p}%` }}
            />
          </div>
          <span className="text-[11px] font-semibold text-gray-500 shrink-0">
            %{p}
          </span>
        </div>
      )}
    </div>
  );
};

const PartCells = {
  part: (row) => {
    const meta = row._meta;
    const sub =
      row.machineType && row.machineType !== "machine"
        ? `${row.machineType}${
            typeof row.index === "number" && row.index > 0 ? ` • #${row.index}` : ""
          }`
        : null;
    return (
      <span className="inline-flex items-center gap-2.5 min-w-0">
        <span className="w-9 h-9 shrink-0 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden">
          {meta.img ? (
            <img
              src={meta.img}
              alt={meta.label}
              className="max-w-full max-h-full object-contain p-0.5"
            />
          ) : (
            <FaCogs className="w-4 h-4 text-slate-400" />
          )}
        </span>
        <span className="inline-flex flex-col items-start min-w-0">
          <span className="text-sm font-bold text-gray-800">{meta.label}</span>
          {sub && <span className="text-[11px] text-gray-400">{sub}</span>}
        </span>
      </span>
    );
  },
  status: (row) => {
    const cfg = row.fault
      ? { label: "Arızalı", cls: "bg-red-100 text-red-700 border-red-200" }
      : {
          label: "Aktif",
          cls: "bg-emerald-100 text-emerald-700 border-emerald-200",
        };
    return (
      <span className="inline-flex flex-col items-start gap-1">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${cfg.cls}`}
        >
          {cfg.label}
        </span>
        {row._nearEnd && !row.fault && (
          <span className="text-[10px] font-semibold text-red-600 whitespace-nowrap">
            Ömrü doluyor
          </span>
        )}
      </span>
    );
  },
  usageCount: (row) => (
    <UsageCell value={row.usageCount} limit={row.usageCountLimit} />
  ),
  usageTime: (row) => (
    <UsageCell value={row.usageTime} limit={row.usageTimeLimit} />
  ),
  production: (row) => {
    const prodDate = fmtDate(row.productionDate);
    const fields = [
      ["Üretim No", row.productionNumber, true],
      ["Ürün Kodu", row.productCode, true],
      ["Üretim Tarihi", prodDate, false],
    ].filter(([, v]) => v);

    if (fields.length === 0) {
      return <span className="text-sm text-gray-400">-</span>;
    }
    return (
      <span className="inline-flex flex-col gap-0.5">
        {fields.map(([label, value, mono]) => (
          <span key={label} className="text-[11px] whitespace-nowrap">
            <span className="text-gray-400">{label}: </span>
            <span className={mono ? "font-mono text-gray-700" : "text-gray-700"}>
              {value}
            </span>
          </span>
        ))}
      </span>
    );
  },
};

const COLUMNS = [
  { key: "part", label: "Parça", cellClassName: "px-4 py-3", render: PartCells.part },
  {
    key: "status",
    label: "Durum",
    cellClassName: "px-4 py-3 align-top",
    render: PartCells.status,
  },
  {
    key: "usageCount",
    label: "Kullanım Sayısı",
    cellClassName: "px-4 py-3 align-top",
    render: PartCells.usageCount,
  },
  {
    key: "usageTime",
    label: "Kullanım Süresi",
    cellClassName: "px-4 py-3 align-top",
    render: PartCells.usageTime,
  },
  {
    key: "production",
    label: "Üretim Bilgisi",
    cellClassName: "px-4 py-3 align-top",
    render: PartCells.production,
  },
];

/**
 * Cihaz "Parçalar" sekmesi — aktif ve arızalı parçaları tablo olarak gösterir.
 * Deaktif/pasif parçalar (active:false, fault:false) gizlenir (değirmen/su
 * modu kararıyla tutarlı).
 *
 * Liste deseni ListToolbar + ListView kompozisyonudur (Kullanıcılar sekmesiyle
 * aynı iskelet). Veri `selectedDevice` prop'undan gelir — ayrı bir sorgu
 * olmadığı için bu sekmenin kendine ait yükleniyor durumu yoktur.
 */
const PartsTab = ({ selectedDevice }) => {
  // Aramalar ayrı alanlarda: tek birleşik kutu "hangi alanda eşleşti"
  // sorusunu belirsiz bırakıyordu; üçü VE ile birleşir.
  const [partSearch, setPartSearch] = useState("");
  const [prodNoSearch, setProdNoSearch] = useState("");
  const [codeSearch, setCodeSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const dPart = useDebounce(partSearch, 300);
  const dProdNo = useDebounce(prodNoSearch, 300);
  const dCode = useDebounce(codeSearch, 300);

  const parts = useMemo(() => {
    const list = selectedDevice?.deviceComponents?.components;
    if (!Array.isArray(list)) return [];
    return list.filter((c) => c && (c.active || c.fault));
  }, [selectedDevice]);

  const allRows = useMemo(
    () =>
      parts.map((c, i) => {
        const countPct = pct(c.usageCount, c.usageCountLimit);
        const timePct = pct(c.usageTime, c.usageTimeLimit);
        return {
          ...c,
          _key: `${c.key}-${c.index ?? 0}-${i}`,
          _meta: getPartMeta(c.key),
          _nearEnd:
            (countPct !== null && countPct >= 90) ||
            (timePct !== null && timePct >= 90),
        };
      }),
    [parts]
  );

  const rows = useMemo(() => {
    const qPart = dPart.trim().toLowerCase();
    const qNo = dProdNo.trim().toLowerCase();
    const qCode = dCode.trim().toLowerCase();
    const has = (v, q) => (v || "").toLowerCase().includes(q);
    return allRows.filter((r) => {
      if (status === "active" && r.fault) return false;
      if (status === "fault" && !r.fault) return false;
      if (qPart && !(has(r._meta.label, qPart) || has(r.machineType, qPart)))
        return false;
      if (qNo && !has(r.productionNumber, qNo)) return false;
      if (qCode && !has(r.productCode, qCode)) return false;
      return true;
    });
  }, [allRows, dPart, dProdNo, dCode, status]);

  // Filtre değişince ilk sayfaya dön — aksi halde boş sayfada kalınır.
  useEffect(() => {
    setPage(1);
  }, [dPart, dProdNo, dCode, status]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [rows, page]
  );

  const hasFilter = Boolean(
    dPart.trim() || dProdNo.trim() || dCode.trim() || status !== "all"
  );

  const renderMobileCard = (row) => (
    <div
      key={row._key}
      className="bg-white border border-gray-200 rounded-xl p-3 space-y-2"
    >
      <div className="flex items-start justify-between gap-2">
        {PartCells.part(row)}
        {PartCells.status(row)}
      </div>
      <div className="grid grid-cols-2 gap-3 pt-1">
        <div>
          <span className="text-[11px] text-gray-400 block mb-0.5">
            Kullanım Sayısı
          </span>
          {PartCells.usageCount(row)}
        </div>
        <div>
          <span className="text-[11px] text-gray-400 block mb-0.5">
            Kullanım Süresi
          </span>
          {PartCells.usageTime(row)}
        </div>
      </div>
      <div className="pt-1 border-t border-gray-100">
        {PartCells.production(row)}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full w-full">
      <ListToolbar
        filters={
          <FilterTabs
            filters={STATUS_FILTERS}
            activeFilter={status}
            onChange={setStatus}
            totalCount={rows.length}
          />
        }
        searchFields={[
          {
            placeholder: "Parça ara...",
            value: partSearch,
            onChange: (e) => setPartSearch(e.target.value),
          },
          {
            placeholder: "Üretim no...",
            value: prodNoSearch,
            onChange: (e) => setProdNoSearch(e.target.value),
          },
          {
            placeholder: "Ürün kodu...",
            value: codeSearch,
            onChange: (e) => setCodeSearch(e.target.value),
          },
        ]}
      />

      <ListView
        columns={COLUMNS}
        data={pageRows}
        rowKey="_key"
        rowClassName={(row) =>
          row.fault
            ? "bg-red-50/40 hover:bg-red-50 transition-colors"
            : "bg-white hover:bg-gray-50 transition-colors"
        }
        renderMobileCard={renderMobileCard}
        empty={
          <EmptyState
            message={hasFilter ? "Parça bulunamadı" : "Aktif parça yok"}
            suggestion={
              hasFilter
                ? "Arama veya filtreyi değiştirmeyi deneyin."
                : "Bu cihaz şu an aktif veya arızalı bir parça bildirmiyor."
            }
          />
        }
        footer={
          totalPages > 1 ? (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              itemsPerPage={PAGE_SIZE}
              totalItems={rows.length}
            />
          ) : null
        }
      />
    </div>
  );
};

export default PartsTab;
